// First-run language resolution. A guest who reads Spanish (or Chinese) should
// LAND in their language — every monument, figure, trésor, parcours, suite and
// conte now speaks it — instead of hunting for the toggle. But an explicit
// choice always outranks a guess.
//
// Order of truth:
//   1. an explicit `?lang=` deep link   (a kiosk QR that promises a language)
//   2. the remembered choice            (localStorage — survives PWA relaunch)
//   3. the device language              (navigator.languages, first offered match)
//   4. French                           (the house default)
//
// The offered set is derived from LANGUAGES so a newly added language (Chinese
// was the drift that motivated this) can never again be silently dropped here.
import { LANGUAGES } from './i18n';

export const LANG_STORE_KEY = 'medard_lang';
export const OFFERED_LANGS = LANGUAGES.map((l) => l.code);

/**
 * Map a BCP-47 tag ('es', 'es-ES', 'zh-CN', 'zh-Hant', 'en-GB') to an offered
 * language code, or null when nothing we serve matches. Only the primary
 * subtag matters — every Chinese variant maps to the one Chinese we offer
 * (Simplified), which still beats defaulting a Chinese speaker to French.
 */
export function normalizeTag(tag, offered = OFFERED_LANGS) {
    if (!tag || typeof tag !== 'string') return null;
    const base = tag.toLowerCase().split('-')[0];
    return offered.includes(base) ? base : null;
}

/** First offered language among the browser's ordered preferences, or null. */
export function detectBrowserLang(navLangs, offered = OFFERED_LANGS) {
    for (const tag of navLangs || []) {
        const code = normalizeTag(tag, offered);
        if (code) return code;
    }
    return null;
}

/**
 * Resolve the initial language from the three signals + the default. Pure: all
 * inputs are passed in, so it is trivially testable and SSR-safe.
 *
 * @param {{urlLang?: string, stored?: string, navLangs?: string[]}} signals
 */
export function resolveInitialLang({ urlLang, stored, navLangs } = {}, offered = OFFERED_LANGS) {
    if (offered.includes(urlLang)) return urlLang;
    if (offered.includes(stored)) return stored;
    return detectBrowserLang(navLangs, offered) || 'fr';
}

// The document's <html lang> must track the ACTIVE UI language, not the static
// "fr" baked into index.html — otherwise a screen reader pronounces an ES/DE/中文
// page with French phonetics, and search engines mis-index it. The value is a
// proper BCP-47 tag: Chinese maps to zh-Hans (the Simplified script we serve),
// the rest are their own primary subtag.
const HTML_LANG_TAG = { fr: 'fr', en: 'en', es: 'es', it: 'it', de: 'de', zh: 'zh-Hans', nl: 'nl' };

/** BCP-47 tag for `<html lang>` given an offered UI-language code. */
export function htmlLangTag(lang) {
    return HTML_LANG_TAG[lang] || lang || 'fr';
}

/**
 * Set `document.documentElement.lang` to the active language's BCP-47 tag.
 * SSR-safe (no-op without a document) and returns the tag it applied (or null).
 */
export function applyDocumentLang(lang) {
    if (typeof document === 'undefined' || !document.documentElement) return null;
    const tag = htmlLangTag(lang);
    document.documentElement.lang = tag;
    return tag;
}
